import React, { useState, useEffect } from "react";
import Plot from "react-plotly.js";
import { Grid, Typography, Paper, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useBatteryConfig } from "../context/BatteryConfigContext";
import { StyledButton, StyledTypography } from "../styles/styles";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const BatteryLifetime = () => {
    const [plotData, setPlotData] = useState({
        capacityVsCycle: null,
        irVsCycle: null,
    });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const { selectedConfig } = useBatteryConfig();
    const navigate = useNavigate();

    useEffect(() => {
        if (!selectedConfig) return;

        const fetchPlotData = async () => {
            if (!selectedConfig.uploadData) {
                setError("No file selected");
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append('num_cols', selectedConfig.num_cols);
            formData.append('totalCells', selectedConfig.totalCells);

            try {
                const response = await axios.post(`${process.env.BACKEND_URL}batteries/pack_lifetime_analysis`, formData, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
                const { capacityVsCycle, irVsCycle } = response.data;

                if (!capacityVsCycle || !irVsCycle) {
                    throw new Error("Plot data is incomplete or incorrect.");
                }

                setPlotData({
                    capacityVsCycle,
                    irVsCycle,
                });
                setError("");
                setLoading(false);
            } catch (error) {
                setError(error.message || "Error fetching plot data. Please check the data and try again.");
                setLoading(false);
            }
        };

        fetchPlotData();
    }, [selectedConfig]);

    const handleNavigateToSubmit = () => {
        navigate("/batteryscope/submit");
    };

    const handleNavigateToLifeTimeAnalysis = () => {
        navigate("/batteryscope/lifetime-analysis-2");
    };


    return (
        <div className="visualize-data" style={{ textAlign: "center", padding: 20 }}>
            {!selectedConfig ? (
                <div className="mt-4">
                    <StyledTypography variant="h6" sx={{ color: "#e8442d" }}>
                        No Battery Data Selected
                    </StyledTypography>
                    <Typography variant="body1" gutterBottom className="mt-2 mb-4">
                        To visualize battery data, please submit your battery configuration first.
                    </Typography>
                    <StyledButton variant="contained" color="primary" onClick={handleNavigateToSubmit}>
                        Go to Submit Page
                    </StyledButton>
                </div>
            ) : loading ? (
                <CircularProgress />
            ) : (
                <>
                    {error ? (
                        <>
                            <Typography variant="body1" color="error" gutterBottom>
                                {error}
                            </Typography>
                            <StyledButton variant="contained" color="primary" onClick={handleNavigateToSubmit}>
                                Go to Submit Page
                            </StyledButton>
                        </>
                    ) : (
                        <>
                            <div style={{ height: "78vh", overflowY: "auto" }}>
                                <div className="flex flex-col gap-3 w-full h-full">
                                    <div className="flex flex-col p-4 max-w-[100wh] max-h-[1100px] justify-center items-center">
                                        {plotData.capacityVsCycle && (
                                            <Plot
                                                data={plotData.capacityVsCycle.data}
                                                layout={{
                                                    ...plotData.capacityVsCycle.layout,
                                                    autosize: true,
                                                }}
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-col p-4 max-w-[100wh] max-h-[1100px] justify-center items-center">
                                        {plotData.irVsCycle && (
                                            <Plot
                                                data={plotData.irVsCycle.data}
                                                layout={{
                                                    ...plotData.irVsCycle.layout,
                                                    autosize: true,
                                                }}
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 flex justify-center">
                                <Typography variant="body1" gutterBottom className="mt-2 mr-2" sx={{ color: "#e8442d", fontFamily: "Bai Jamjuree" }}>
                                    Click on the plots to zoom in/out.
                                </Typography>
                                <StyledButton variant="contained" onClick={handleNavigateToLifeTimeAnalysis} className="gap-2">
                                    Go to Life Time Analysis
                                    <ArrowForwardIcon />
                                </StyledButton>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default BatteryLifetime;
